/**
 * AvatarStack displays a horizontal, overlapping stack of user avatars with optional tooltips, overflow, and custom styling.
 *
 * Features:
 * - Overlapping avatars with spacing and size variants
 * - Tooltip with user info on hover/focus
 * - Overflow indicator with tooltip for extra users
 * - Customizable border, color, RTL, and click handlers
 * - Responsive and accessible
 */
import {
    Avatar,
    Colors,
    Text,
    type ColorType,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';

import type { User } from '@/types/directory/User';
import type { AvatarBorderSize, AvatarSize } from '@amelio/ui-library';

import { getInitials, getProfilePictureUrl } from '@/utils/user';

/**
 * Props for AvatarStack component.
 * @property users - Array of user objects to display as avatars.
 * @property maxVisible - Maximum number of avatars to show before overflow indicator (default: all).
 * @property size - Size of avatars: 'sm', 'md', or 'lg' (default: 'md').
 * @property spacing - Overlap spacing: 'tight', 'normal', or 'loose' (default: 'normal').
 * @property showBorder - Whether to show a border around each avatar (default: true).
 * @property borderColor - HEX color value from Colors (e.g., Colors.PRIMARY, Colors.SECONDARY_600) for avatar border (default: Colors.BASE_50).
 * @property borderSize - Border size: 'none' (no border), 'ring-1' (1px), 'ring-2' (2px), 'ring-3' (3px), 'ring-4' (4px) (default: 'ring-2').
 * @property onAvatarClick - Callback when an avatar is clicked.
 * @property onOverflowClick - Callback when overflow indicator is clicked.
 * @property className - Additional className for the stack container.
 * @property rtl - If true, stack avatars right-to-left (default: false).
 * @property showTooltip - Show tooltip with user info (default: true).
 * @property dataTestId - Optional data-testid prefix for testing purposes.
 */
export interface AvatarStackProps {
    users: User[];
    maxVisible?: number;
    size?: '4xs' | '3xs' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    spacing?: 'tight' | 'normal' | 'loose';
    showBorder?: boolean;
    borderColor?: ColorType;
    borderSize?: AvatarBorderSize;
    onAvatarClick?: (user: User) => void;
    onOverflowClick?: (remainingUsers: User[]) => void;
    className?: string;
    rtl?: boolean;
    showTooltip?: boolean;
    dataTestId?: string;
}

/**
 * Maps AvatarStack size prop to Avatar size and overflow indicator classes.
 */
const AVATAR_SIZE_MAP = {
    '4xs': { avatar: '4xs', overflow: 'h-4 w-4 text-xs' },
    '3xs': { avatar: '3xs', overflow: 'h-5 w-5 text-xs' },
    '2xs': { avatar: '2xs', overflow: 'h-6 w-6 text-xs' },
    xs: { avatar: 'xs', overflow: 'h-6 w-6 text-xs' },
    sm: { avatar: 'sm', overflow: 'h-6 w-6 text-xs' },
    md: { avatar: 'md', overflow: 'h-8 w-8 text-sm' },
    lg: { avatar: 'lg', overflow: 'h-10 w-10 text-base' },
    xl: { avatar: 'xl', overflow: 'h-12 w-12 text-base' },
};

/**
 * Maps AvatarStack size and spacing prop to negative margin values for overlap.
 */
const SIZE_SPACING_MAP = {
    '4xs': { loose: -2, normal: -4, tight: -6 },
    '3xs': { loose: -2, normal: -4, tight: -6 },
    '2xs': { loose: -3, normal: -5, tight: -7 },
    xs: { loose: -4, normal: -6, tight: -8 },
    sm: { loose: -5, normal: -7, tight: -9 },
    md: { loose: -6, normal: -8, tight: -10 },
    lg: { loose: -7, normal: -9, tight: -11 },
    xl: { loose: -8, normal: -10, tight: -12 },
};

/**
 * Renders a horizontal, overlapping stack of user avatars with tooltips and overflow handling.
 *
 * @param users - Array of user objects to display as avatars.
 * @param maxVisible - Maximum number of avatars to show before overflow indicator.
 * @param size - Size of avatars: 'sm', 'md', or 'lg'.
 * @param spacing - Overlap spacing: 'tight', 'normal', or 'loose'.
 * @param showBorder - Whether to show a border around each avatar.
 * @param borderColor - HEX color value from Colors (e.g., Colors.PRIMARY, Colors.SECONDARY_600) for avatar border.
 * @param borderSize - Border size: 'none' (no border), 'ring-1' (1px), 'ring-2' (2px), 'ring-3' (3px), 'ring-4' (4px).
 * @param onAvatarClick - Callback when an avatar is clicked.
 * @param onOverflowClick - Callback when overflow indicator is clicked.
 * @param className - Additional className for the stack container.
 * @param rtl - If true, stack avatars right-to-left.
 * @param showTooltip - Show tooltip with user info.
 * @param dataTestId - Optional data-testid prefix for testing purposes.
 */
export const AvatarStack = ({
    users = [],
    maxVisible,
    size = 'md',
    spacing = 'normal',
    showBorder = true,
    borderColor = Colors.BASE_50,
    borderSize = 'ring-2',
    onAvatarClick,
    onOverflowClick,
    className = '',
    rtl = false,
    showTooltip = true,
    dataTestId = '',
}: AvatarStackProps) => {
    if (!users || users.length === 0) return null;
    const shownUsers = maxVisible ? users.slice(0, maxVisible) : users;
    const hiddenUsers = maxVisible ? users.slice(maxVisible) : [];
    const sizeConfig = AVATAR_SIZE_MAP[size];
    const overlap = SIZE_SPACING_MAP[size][spacing];
    const stackOrder = rtl ? [...shownUsers].reverse() : shownUsers;

    return (
        <div
            className={`relative flex w-fit items-center overflow-visible ${className}`}
            style={{ display: 'flex', flexDirection: rtl ? 'row-reverse' : 'row' }}
            data-testid={dataTestId ? `${dataTestId}-avatar-stack` : undefined}
        >
            <TooltipProvider>
                {stackOrder.map((user, idx) => (
                    <Tooltip
                        key={user.id}
                        delayDuration={200}
                    >
                        <TooltipTrigger asChild>
                            <div
                                className={`relative inline-block`}
                                tabIndex={0}
                                style={{
                                    marginLeft: !rtl && idx === 0 ? 0 : !rtl ? overlap : 0,
                                    marginRight: rtl && idx === 0 ? 0 : rtl ? overlap : 0,
                                    zIndex: users.length + idx,
                                }}
                                onClick={() => onAvatarClick?.(user)}
                                onMouseEnter={(e) => (e.currentTarget.style.zIndex = '9999')}
                                onMouseLeave={(e) =>
                                    (e.currentTarget.style.zIndex = String(users.length + idx))
                                }
                                data-testid={`${dataTestId}-avatar-container-${user.id}`}
                            >
                                <Avatar
                                    alt={'profile-pic'}
                                    initials={getInitials(user)}
                                    src={getProfilePictureUrl(user?.profilePictureUrl || '')}
                                    className={'bg-base-50'}
                                    size={sizeConfig.avatar as AvatarSize}
                                    borderColor={showBorder ? borderColor : undefined}
                                    borderSize={showBorder ? borderSize : undefined}
                                    dataTestId={`${dataTestId}-avatar-${user.id}`}
                                />
                            </div>
                        </TooltipTrigger>

                        {showTooltip && (
                            <TooltipContent
                                className={'pointer-events-auto max-w-xs'}
                                side={'top'}
                            >
                                {`${user.firstName} ${user.lastName}`}
                            </TooltipContent>
                        )}
                    </Tooltip>
                ))}
            </TooltipProvider>

            {maxVisible && users.length > maxVisible && (
                <TooltipProvider>
                    <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                            <div
                                className={`relative inline-block`}
                                tabIndex={0}
                                style={{
                                    marginLeft: !rtl ? overlap : 0,
                                    marginRight: rtl ? overlap : 0,
                                    zIndex: users.length + maxVisible,
                                }}
                                onClick={() => onOverflowClick?.(hiddenUsers)}
                                data-testid={`${dataTestId}-overflow-container`}
                            >
                                <Avatar
                                    alt={'profile-pic'}
                                    initials={`+${users.length - maxVisible}`}
                                    className={`${sizeConfig.overflow} text-xs`}
                                    size={sizeConfig.avatar as AvatarSize}
                                    borderColor={undefined}
                                    borderSize={'none'}
                                />
                            </div>
                        </TooltipTrigger>

                        {showTooltip && (
                            <TooltipContent
                                className={
                                    'scrollbar-glass pointer-events-auto max-h-87 max-w-xs overflow-y-auto'
                                }
                                side={'top'}
                                layout={'list'}
                            >
                                <div className={'space-y-2 p-5'}>
                                    {hiddenUsers.map((user) => (
                                        <div
                                            key={user.id}
                                            className={'flex flex-row items-center pb-4'}
                                        >
                                            <Avatar
                                                alt={'profile-pic'}
                                                initials={getInitials(user)}
                                                src={getProfilePictureUrl(
                                                    user?.profilePictureUrl || '',
                                                )}
                                                className={'bg-base-50 mr-2'}
                                                size={'4xs'}
                                            />

                                            <Text size={'small'}>
                                                {`${user.firstName} ${user.lastName}`}
                                            </Text>
                                        </div>
                                    ))}
                                </div>
                            </TooltipContent>
                        )}
                    </Tooltip>
                </TooltipProvider>
            )}
        </div>
    );
};
